/**
 * El IVA de un cobro (24/9/2026)
 * ---------------------------------------------------
 * «Cuando se añade un tratamiento y se marca "con IVA", el importe
 * introducido es el precio final que paga el paciente (PVP). El sistema no
 * debe sumar el 21%: debe extraer el IVA de dentro de esa cantidad.»
 *
 *   total = importe escrito
 *   base  = total / 1,21, redondeado al céntimo
 *   IVA   = total − base            (base + IVA da siempre el total)
 *
 * Es LA MISMA cuenta que `backend/services/iva.py`, con el mismo redondeo y
 * en céntimos enteros. La que manda es la del servidor: esta solo enseña el
 * desglose mientras se escribe. Los casos de prueba son los mismos para las
 * dos (`ivaCasos.json`).
 *
 * Los cobros guardados antes del 24/9/2026 (sin la marca `iva_incluido`)
 * llevaban el 21% ENCIMA del importe y guardaron su base y su total ya
 * calculados. No se recalculan: se enseñan tal cual (`desgloseGuardado`).
 */

export const TIPO_IVA = 0.21;

/** Euros → céntimos enteros, la mitad hacia arriba (en valor absoluto). */
export function aCentimos(importe) {
  const x = Number(importe || 0);
  if (!Number.isFinite(x)) return 0;
  const signo = x < 0 ? -1 : 1;
  return signo * Math.floor(Math.abs(x) * 100 + 0.5);
}

/** Base imponible, IVA y total de un importe que YA lleva el IVA dentro. */
export function desgloseIvaIncluido(importe, conIva) {
  const totalC = aCentimos(importe);
  let baseC = totalC;
  if (conIva) {
    const signo = totalC < 0 ? -1 : 1;
    const t = Math.abs(totalC);
    // round(t / 1,21) con enteros: floor((t·200 + 121) / 242). Nunca empata.
    baseC = signo * Math.floor((t * 200 + 121) / 242);
  }
  const ivaC = totalC - baseC;
  return { base: baseC / 100, iva: ivaC / 100, total: totalC / 100 };
}

/** ¿Se apuntó este cobro con el IVA dentro del importe (desde el 24/9/2026)? */
export function esIvaIncluido(cobro) {
  return !!cobro?.iva_incluido;
}

/**
 * Base, IVA y total de un cobro TAL COMO SE GUARDÓ, sin recalcular nada:
 * vale igual para los de antes (21% encima) y los de ahora (IVA dentro).
 */
export function desgloseGuardado(cobro) {
  const total = Number(cobro?.total ?? cobro?.amount ?? 0);
  const base = Number(cobro?.amount_base ?? cobro?.amount ?? 0);
  const iva = cobro?.vat_amount != null
    ? Number(cobro.vat_amount)
    : (aCentimos(total) - aCentimos(base)) / 100;
  return { base, iva, total };
}
